import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getArticleBySlug, getWebsiteContent } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { ContentContext } from '../App';

const BlogArticlePage = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const [article, setArticle] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [articleRes, contentRes] = await Promise.all([
          getArticleBySlug(slug),
          getWebsiteContent()
        ]);
        setArticle(articleRes.data);
        setContent(contentRes.data);
      } catch (error) {
        console.error('Failed to load article:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!article) {
    return (
      <ContentContext.Provider value={content}>
        <div className="min-h-screen bg-slate-50">
          <Header />
          <div className="pt-32 pb-16 text-center">
            <h1 className="text-2xl font-bold text-slate-900">Article not found</h1>
            <Link to="/blog" className="text-amber-600 mt-4 inline-block">Back to articles</Link>
          </div>
          <Footer />
        </div>
      </ContentContext.Provider>
    );
  }

  const title = language === 'en' ? article.title_en : article.title_zh;
  const contentText = language === 'en' ? article.content_en : article.content_zh;

  return (
    <ContentContext.Provider value={content}>
      <div className="min-h-screen bg-slate-50">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-32 pb-8 bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back Link */}
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-400 mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {language === 'en' ? 'Back to Articles' : '返回文章列表'}
            </Link>

            <div className="flex items-center gap-2 text-amber-400 text-sm mb-4">
              <BookOpen className="w-4 h-4" />
              <span>{language === 'en' ? 'Article' : '文章'}</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
              {title}
            </h1>
          </div>
        </section>

        {/* Featured Image */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4">
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img
              src={article.image_url}
              alt={title}
              className="w-full h-64 sm:h-80 object-cover"
            />
          </div>
        </div>

        {/* Article Content */}
        <article className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl shadow-sm p-8 sm:p-12">
              <div className="prose prose-lg max-w-none prose-headings:text-slate-900 prose-p:text-slate-600 prose-strong:text-slate-900">
                {contentText.split('\n\n').map((paragraph, index) => {
                  if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                    // Section heading
                    return (
                      <h3 key={index} className="text-xl font-semibold text-slate-900 mt-8 mb-4">
                        {paragraph.replace(/\*\*/g, '')}
                      </h3>
                    );
                  } else if (paragraph.includes('**')) {
                    // Heading within paragraph
                    const parts = paragraph.split('**');
                    return (
                      <div key={index} className="mb-6">
                        {parts.map((part, i) => (
                          i % 2 === 1 ? (
                            <h4 key={i} className="text-lg font-semibold text-slate-900 mt-6 mb-2">{part}</h4>
                          ) : (
                            <p key={i} className="text-slate-600 leading-relaxed">{part}</p>
                          )
                        ))}
                      </div>
                    );
                  } else if (paragraph.startsWith('- ')) {
                    // List item
                    return (
                      <ul key={index} className="list-disc list-inside text-slate-600 space-y-2 mb-6">
                        {paragraph.split('\n').map((item, i) => (
                          <li key={i}>{item.replace('- ', '')}</li>
                        ))}
                      </ul>
                    );
                  } else {
                    return (
                      <p key={index} className="text-slate-600 leading-relaxed mb-6">
                        {paragraph}
                      </p>
                    );
                  }
                })}
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 bg-amber-500 rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                {language === 'en' ? 'Need Roofing Help?' : '需要屋顶帮助？'}
              </h3>
              <p className="text-slate-800 mb-6">
                {language === 'en' 
                  ? 'Contact our expert team for a free consultation and quote.'
                  : '联系我们的专业团队获取免费咨询和报价。'}
              </p>
              <Link
                to="/#contact"
                className="inline-block bg-slate-900 hover:bg-slate-800 text-white font-semibold px-8 py-4 rounded-xl transition-colors"
              >
                {language === 'en' ? 'Get Free Quote' : '获取免费报价'}
              </Link>
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </ContentContext.Provider>
  );
};

export default BlogArticlePage;
